import React from 'react';
import { lazy, Suspense } from 'react';
import { sizes, colors } from './styles';
import { IIcon } from './types';
import { useStyletron } from '../../Themes';
import { Warning } from '../../Icons/Warning';

export const _MIcon = React.forwardRef((props: Omit<IIcon, 'css'>, ref: any) => {
	const { size = 'small', color = 'inherit', icon, className, ...rest } = props;
	const [css] = useStyletron();

	const isPresetSize = ['x-small', 'small', 'medium', 'large', 'x-large'].includes(size);
	const sizeDefinition = isPresetSize ? sizes[size] : size;

	const isPresetColor = ['validated', 'error', 'warning', 'information'].includes(color);
	const colorDefinition = isPresetColor ? colors[color] : color;

	return (
		<i
			ref={ref}
			aria-hidden="false"
			className={`${className} ${css({
				fontSize: sizeDefinition,
				width: sizeDefinition,
				height: sizeDefinition,
				MinWidth: sizeDefinition,
				MinHeight: sizeDefinition,
				MaxWidth: sizeDefinition,
				MaxHeight: sizeDefinition,
				boxSizing: 'content-box',
				color: colorDefinition,
				display: 'inline-block',
			})} epo-icons icon-${icon}`}
			{...rest}
		/>
	);
});

export const MIcon = React.memo(_MIcon);

export const NoIcon = React.forwardRef((props: any, ref: any) => {
	const { size = 'small', color, style, className, ...rest } = props;
	const [css] = useStyletron();

	const isPresetSize = ['x-small', 'small', 'medium', 'large', 'x-large'].includes(size);
	const sizeDefinition = isPresetSize ? sizes[size] : size;

	return (
		<span
			ref={ref}
			aria-hidden="true"
			className={`${className} ${css({
				...{
					width: sizeDefinition,
					height: sizeDefinition,
					MinWidth: sizeDefinition,
					MinHeight: sizeDefinition,
					MaxWidth: sizeDefinition,
					MaxHeight: sizeDefinition,
					boxSizing: 'content-box',
				},
				...style,
			})}`}
			{...rest}
		></span>
	);
});

class ErrorBoundary extends React.Component<{}, { hasError: boolean }> {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error) {
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return <Warning />;
		}

		return this.props.children;
	}
}

export const DIcon = ({ icon, size = '18px', path = '@epo/epods-react-components/lib/Icons/', ...rest }: any) => {
	const isPresetSize = ['x-small', 'small', 'medium', 'large', 'x-large'].includes(size);
	const sizeDefinition = isPresetSize ? sizes[size] : size;
	const pascalCase = (s: string) => {
		const _camelCase = s.replace(/([-_][a-z])/gi, $1 => {
			return $1
				.toUpperCase()
				.replace('-', '')
				.replace('_', '');
		});
		return _camelCase.charAt(0).toUpperCase() + _camelCase.slice(1);
	};

	// Storybook not working, even giving a path "../../Icons/"
	// TODO: Need to test on real app
	// If not working, this DIcon component have to be copied and pasted into projects
	let MyComponent = lazy(() => import(path + pascalCase(icon)));

	return (
		<ErrorBoundary>
			<Suspense
				fallback={
					<span style={{ display: 'inline-block', width: sizeDefinition, height: sizeDefinition }}></span>
				}
			>
				<MyComponent fontSize={size} {...rest} />
			</Suspense>
		</ErrorBoundary>
	);
};
